import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'
import { Topbar } from '../components/Topbar'
import { ArrowLeft, Save, Loader2 } from 'lucide-react'
import { apiGet } from '../lib/api'

interface MarcaDetail {
  id: string
  nombre: string
  slug: string
  logo: string
  activa: boolean
  productCount: number
}

interface FormState {
  name: string
  slug: string
  logo: string
  is_active: boolean
}

const empty: FormState = { name: '', slug: '', logo: '', is_active: true }

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}

export function MarcaForm() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const params = useParams({ strict: false }) as { id?: string }
  const idParam = params.id
  const isEdit = idParam && idParam !== 'nueva'
  const [form, setForm] = useState<FormState>(empty)
  const [slugTouched, setSlugTouched] = useState(false)
  const [error, setError] = useState('')

  const detailQuery = useQuery<MarcaDetail>({
    queryKey: ['marca', idParam],
    queryFn: () => apiGet(`/api/admin/marcas/${idParam}`),
    enabled: !!isEdit,
  })

  useEffect(() => {
    if (detailQuery.data) {
      const d = detailQuery.data
      setForm({ name: d.nombre, slug: d.slug, logo: d.logo ?? '', is_active: d.activa })
      setSlugTouched(true)
    }
  }, [detailQuery.data])

  function update<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((s) => ({ ...s, [k]: v }))
  }

  function onChangeName(v: string) {
    update('name', v)
    if (!slugTouched && !isEdit) update('slug', slugify(v))
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const url = isEdit ? `/api/admin/marcas/${idParam}` : '/api/admin/marcas'
      const method = isEdit ? 'PUT' : 'POST'
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: 'Error' }))
        throw new Error(err.error || 'Error guardando')
      }
      return r.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marcas'] })
      navigate({ to: '/marcas' })
    },
    onError: (e: Error) => setError(e.message),
  })

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (!form.name.trim()) {
      setError('El nombre es obligatorio')
      return
    }
    if (!/^[a-z0-9-]+$/.test(form.slug)) {
      setError('El slug solo puede contener letras minúsculas, números y guiones')
      return
    }
    mutation.mutate()
  }

  const title = isEdit ? 'Editar marca' : 'Nueva marca'

  if (isEdit && detailQuery.isLoading) {
    return (
      <>
        <Topbar title={title} />
        <div className="grid place-items-center p-20 text-ink-500">
          <Loader2 className="size-8 animate-spin" />
        </div>
      </>
    )
  }

  return (
    <>
      <Topbar
        title={title}
        subtitle={isEdit ? `ID: ${idParam}` : 'Crear una nueva marca de repuesto'}
        actions={
          <button
            type="button"
            onClick={() => navigate({ to: '/marcas' })}
            className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
          >
            <ArrowLeft className="size-3.5" /> Volver
          </button>
        }
      />
      <form onSubmit={onSubmit} className="p-6">
        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mx-auto max-w-2xl space-y-6">
          <div className="rounded-2xl border border-ink-200 bg-white p-6">
            <h2 className="mb-4 text-sm font-semibold text-ink-900">Información de la marca</h2>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-ink-700">
                  Nombre <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => onChangeName(e.target.value)}
                  required
                  maxLength={80}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-ink-700">
                  Slug <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => {
                    setSlugTouched(true)
                    update('slug', e.target.value)
                  }}
                  required
                  pattern="[a-z0-9-]+"
                  maxLength={80}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 font-mono text-sm outline-none focus:border-brand-500"
                />
                <p className="mt-1 text-xs text-ink-500">
                  URL amigable, solo minúsculas, números y guiones
                </p>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-ink-700">
                  Logo (URL)
                </label>
                <div className="flex items-center gap-3">
                  <div className="grid size-16 shrink-0 place-items-center overflow-hidden rounded-lg border border-ink-200 bg-white">
                    {form.logo ? (
                      <img
                        src={form.logo}
                        alt="logo"
                        className="max-h-full max-w-full object-contain"
                        onError={(e) => {
                          ;(e.target as HTMLImageElement).style.display = 'none'
                        }}
                      />
                    ) : (
                      <span className="text-[10px] text-ink-400">sin logo</span>
                    )}
                  </div>
                  <input
                    type="url"
                    value={form.logo}
                    onChange={(e) => update('logo', e.target.value)}
                    placeholder="/ruta/logo.png  o  https://…"
                    maxLength={255}
                    className="flex-1 rounded-lg border border-ink-200 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-brand-500"
                  />
                </div>
                <p className="mt-1 text-xs text-ink-500">
                  Aparece en el carrusel de marcas del home.
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => update('is_active', e.target.checked)}
                  className="size-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                />
                Marca activa
              </label>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={mutation.isPending}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {mutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Save className="size-4" />
              )}
              {isEdit ? 'Guardar cambios' : 'Crear marca'}
            </button>
            <button
              type="button"
              onClick={() => navigate({ to: '/marcas' })}
              className="rounded-lg border border-ink-200 bg-white px-4 py-2.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              Cancelar
            </button>
          </div>
        </div>
      </form>
    </>
  )
}
